import { useRef, useEffect, useState } from "react";

import './letters.css';

export default function LagrangePointsSimulation({
  numParticles = 500,
  highlightRadius = 12,
  particleColor = "#888",
  lagrangeParticleColor = "#00ff88",
  background = "black",
}) {
  const canvasRef = useRef(null);

  const [isFrameRotating, setToggleFrame] = useState(false);
  const G = 1;
  const timeStep = 0.101;

  const sun = {
    mass: 10000,
    position: { x: -50, y: 0 },
    velocity: { x: 0, y: 0 },
    radius: 50,
    color: "#ffaa00",
  };

  const planet = {
    mass: 1,
    position: { x: 250, y: 0 },
    velocity: { x: 0, y: 4 },
    radius: 10,
    color: "#55aaff",
  };
  const particlesSize = 7;

  const bodiesRef = useRef([sun, planet]);

  const particlesRef = useRef(
    Array.from({ length: numParticles }, () => {
      const angle = Math.random() * Math.PI * 2;
      const r = 220 + Math.random() * 80;
      return {
        position: {
          x: Math.cos(angle) * r,
          y: Math.sin(angle) * r,
        },
        velocity: {
          x: -Math.sin(angle) * 2.5,
          y: Math.cos(angle) * 2.5,
        },
      };
    })
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const step = () => {
      const [sun, planet] = bodiesRef.current;

      /* ---------- SUN + PLANET ---------- */
      const dx = planet.position.x - sun.position.x;
      const dy = planet.position.y - sun.position.y;
      const r2 = dx * dx + dy * dy;
      const r = Math.sqrt(r2);

      const ax = (G * dx) / (r2 * r);
      const ay = (G * dy) / (r2 * r);

      sun.velocity.x += planet.mass * ax * timeStep;
      sun.velocity.y += planet.mass * ay * timeStep;
      planet.velocity.x -= sun.mass * ax * timeStep;
      planet.velocity.y -= sun.mass * ay * timeStep;

      sun.position.x += sun.velocity.x * timeStep;
      sun.position.y += sun.velocity.y * timeStep;
      planet.position.x += planet.velocity.x * timeStep;
      planet.position.y += planet.velocity.y * timeStep;

      /* ---------- ANGULAR VELOCITY ---------- */
      const omega = (dx * (planet.velocity.y - sun.velocity.y) -
        dy * (planet.velocity.x - sun.velocity.x)) / r2;

      const cx =
        (sun.position.x * sun.mass +
          planet.position.x * planet.mass) /
        (sun.mass + planet.mass);
      const cy =
        (sun.position.y * sun.mass +
          planet.position.y * planet.mass) /
        (sun.mass + planet.mass);

      /* ---------- PARTICLES ---------- */
      particlesRef.current.forEach((p) => {
        let ax = 0;
        let ay = 0;

        // Gravity
        [sun, planet].forEach((b) => {
          const dx = b.position.x - p.position.x;
          const dy = b.position.y - p.position.y;
          const r2 = dx * dx + dy * dy + 0.01;
          const r = Math.sqrt(r2);

          ax += (G * b.mass * dx) / (r2 * r);
          ay += (G * b.mass * dy) / (r2 * r);
        });

        if (isFrameRotating) {
          const rx = p.position.x - cx;
          const ry = p.position.y - cy;

          // Coriolis
          ax += 2 * omega * p.velocity.y;
          ay -= 2 * omega * p.velocity.x;

          // Centrifugal
          ax += omega * omega * rx;
          ay += omega * omega * ry;
        }

        p.velocity.x += ax * timeStep;
        p.velocity.y += ay * timeStep;
        p.position.x += p.velocity.x * timeStep;
        p.position.y += p.velocity.y * timeStep;
      });

      /* ---------- DRAW ---------- */
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);

      // Sun + planet
      bodiesRef.current.forEach((b) => {
        ctx.fillStyle = b.color;
        ctx.beginPath();
        ctx.arc(b.position.x, b.position.y, b.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // Particles
      particlesRef.current.forEach((p) => {
        ctx.fillStyle = particleColor;
        ctx.fillRect(p.position.x, p.position.y, particlesSize, particlesSize);
      });

      ctx.restore();
      requestAnimationFrame(step);
    };

    step();
    return () => window.removeEventListener("resize", resize);
  }, [
    numParticles,
    highlightRadius,
    particleColor,
    lagrangeParticleColor,
    background,
  ]);

  useEffect(() => {
    function handleClickCanvas(e) { setToggleFrame(s => !s); }

    window.addEventListener("click", handleClickCanvas);
    return () => window.removeEventListener("click", handleClickCanvas);
  }, [setToggleFrame]);

  return (
    <div className="letter-grid">
      <canvas
        ref={canvasRef}
        style={{ width: "100vw", height: "100vh", display: "block" }}
      />
    </div>
  );
}
